import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button, Col, Form, Input, Modal, Row, Select } from "antd";
import useGet from "../../../global/hooks/useGet";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { CloseOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import { FaFileAlt } from "react-icons/fa";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import { convertToUnix } from "../../../global/utils/unixToLocalTime";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import dayjs from "dayjs";
import { DeviceProductionColumns } from "./Columns";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { hasRole } from "../../../global/hooks/getUserRole";
import { DEVICE_PRODUCTION_LIST_SEARCH_KEY } from "../utils/listSearchStorage";
import { downloadDeviceProductionCSV } from "../utils/downloadProductionDeviceCSV";
import { downloadDeviceProductionPDF } from "../utils/downloadProductionDevicePDF ";

const DEVICE_PRODUCTION_BASE = `${process.env.REACT_APP_API_URL}/api/device-production`;

const initialFilterState = {
  ProductionNo: "",
  ProductionName: "",
  ProductionTime: "",
  HardwareNo: "",
  LicenseNo: "",
};

const DeviceProduction = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "ProductionNo",
    order: "desc",
  });

  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {};
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(initialFilterState);
  const [showModal, setShowModal] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportFile, setReportFile] = useState(1);
  const [form] = Form.useForm();
  const [formFilter] = Form.useForm();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    sessionStorage.setItem(
      DEVICE_PRODUCTION_LIST_SEARCH_KEY,
      location.search || "",
    );
  }, [location.search]);

  const query = new URLSearchParams(params);
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${DEVICE_PRODUCTION_BASE}/?${updatedQuery}`
    : `${DEVICE_PRODUCTION_BASE}/`;
  const { data, loading, refetch } = useGet(url);

  const hardwareUrl = `${process.env.REACT_APP_API_URL}/api/hardwares/?limit=100&order=desc`;
  const { data: hardwareData } = useGet(hardwareUrl);
  const hardwareOptions =
    hardwareData?.data?.map((item) => ({
      label: item.HardwareName,
      value: item.HardwareNo,
    })) || [];

  const optionUrl = `${process.env.REACT_APP_API_URL}/api/dealers/options/`;
  const { data: optionData } = useGet(optionUrl);
  const licenseOptions = getModifiedResponse(optionData, "licenses");

  const csvQuery = new URLSearchParams(params);
  csvQuery.delete("pageSize");
  csvQuery.delete("total");
  csvQuery.delete("current");
  csvQuery.delete("limit");
  csvQuery.append("production_ids", selectedRowKeys);
  const updatedCsvQuery = csvQuery.toString();
  const exportUrl = updatedCsvQuery
    ? `${DEVICE_PRODUCTION_BASE}/export/?${updatedCsvQuery}`
    : `${DEVICE_PRODUCTION_BASE}/export/`;
  const { refetch: refetchExport, loading: exportLoading } = useGet(
    shouldFetch ? exportUrl : null,
  );

  let deleteUrl;
  selectedRowKeys.length > 1
    ? (deleteUrl = `${DEVICE_PRODUCTION_BASE}/bulk-delete/`)
    : (deleteUrl = `${DEVICE_PRODUCTION_BASE}/${selectedRowKeys}/`);

  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
    deleteMultiple,
  } = useDelete(deleteUrl);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }
    setFilter((prev) => ({ ...prev, ...matchedFilter }));
  }, []);

  useEffect(() => {
    const formValues = {};
    if (modParams?.ProductionTime) {
      formValues.ProductionTime = dayjs.unix(Number(modParams.ProductionTime));
    }
    if (Object.keys(formValues).length > 0) {
      formFilter.setFieldsValue(formValues);
    }
  }, [params]);

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({ ...pagination, current: page, pageSize });
      setParams({
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page,
      });
    },
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
    fixed: true,
  };

  const onRow = (record) => ({
    onClick: () => navigate(`/device-production/info/${record?.ProductionNo}`),
  });

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFormChange = (changedValues) => {
    if (changedValues.ProductionTime !== undefined) {
      setFilter((prev) => ({
        ...prev,
        ProductionTime: changedValues.ProductionTime,
      }));
    }
  };

  const handleFilter = () => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        if (key === "ProductionTime") {
          filterDict[key] = convertToUnix(filter[key]);
        } else {
          filterDict[key] = filter[key];
        }
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";
    if (isEmpty) {
      handleReset();
    } else {
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams({
        limit: 10,
        sort_by: "ProductionNo",
        order: "desc",
        page: 1,
        ...filterDict,
      });
      refetch();
    }
  };

  const handleReset = () => {
    setFilter(initialFilterState);
    setPagination({ current: 1, pageSize: 10, total: 0 });
    setParams({ limit: 10, sort_by: "ProductionNo", order: "desc" });
    refetch();
    setSelectedRowKeys([]);
  };

  const onDeleteConfirm = async () => {
    try {
      if (selectedRowKeys.length === 1) await deleteData();
      else await deleteMultiple({ ids: selectedRowKeys });
    } catch {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      setListData(
        data?.data?.map((el) => ({
          ...el,
          key: el?.ProductionNo,
        })) || [],
      );
    }
  }, [data, loading]);

  const handleExportClick = async (format = 1) => {
    try {
      const freshData = await refetchExport();
      if (!freshData) {
        throw new Error("No export data found");
      }
      const timestamp = new Date().toISOString().split("T")[0];
      if (format === 1) {
        downloadDeviceProductionCSV(
          freshData,
          `Device-Production-Report_${timestamp}.csv`,
        );
      } else if (format === 2) {
        downloadDeviceProductionPDF(
          freshData,
          `Device-Production-Report_${timestamp}.pdf`,
        );
      }
      openConfirmNotification("Success");
    } catch (error) {
      console.error("Export error:", error);
      openErrorNotification(error.message || "Something went wrong.");
    }
    setShouldFetch(false);
  };

  const breadcrumbItems = [
    { label: "Device Production", href: "/device-production" },
  ];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <div className="mb-4">
          <Form
            className=""
            form={formFilter}
            onValuesChange={handleFormChange}
          >
            <div className="mt-2">
              <Row gutter={[16, 16]}>
                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  lg={8}
                  xl={6}
                  className="!h-[32px]"
                >
                  <Input
                    className="input_text_3 mr10 h-full"
                    placeholder="No"
                    value={filter.ProductionNo}
                    onChange={(e) => {
                      const { value } = e.target;
                      // Allow only positive integers or empty string
                      if (/^[1-9]\d*$/.test(value) || value === "") {
                        handleFilterChange(e);
                      }
                    }}
                    name="ProductionNo"
                  />
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  lg={8}
                  xl={6}
                  className="!h-[32px]"
                >
                  <Input
                    className="input_text_3 mr10 h-full"
                    placeholder="Name"
                    value={filter.ProductionName}
                    onChange={handleFilterChange}
                    name="ProductionName"
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                  <CustomDateTimePicker
                    name="ProductionTime"
                    placeholder="Production Time"
                    format="YYYY-MM-DD HH:mm"
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                  <Select
                    placeholder="Hardware"
                    allowClear
                    style={{ width: "100%" }}
                    value={filter.HardwareNo ? Number(filter.HardwareNo) : null}
                    onChange={(value) =>
                      setFilter({ ...filter, HardwareNo: value ?? "" })
                    }
                    options={hardwareOptions}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                  <Select
                    placeholder="License"
                    allowClear
                    style={{ width: "100%" }}
                    value={filter.LicenseNo ? Number(filter.LicenseNo) : null}
                    onChange={(value) =>
                      setFilter({ ...filter, LicenseNo: value ?? "" })
                    }
                    options={licenseOptions}
                  />
                </Col>
              </Row>
            </div>

            <div className="w-full overflow-x-auto">
              <div className="flex justify-between my-4 flex-nowrap min-w-[610px]">
                <div className="flex items-center">
                  <Button
                    type="primary"
                    className="mr-2 w-[104px] h-[30px] font-medium"
                    onClick={handleFilter}
                  >
                    <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                    Apply
                  </Button>
                  <Button
                    className="reset-button"
                    onClick={() => {
                      formFilter.resetFields();
                      handleReset();
                    }}
                  >
                    <HiArrowPath className="!text-[14px] font-medium" />
                    Reset
                  </Button>
                  <Button
                    type="primary"
                    className="mr-2 -ml-2 w-[104px] h-[30px] font-medium"
                    onClick={() => navigate("/device-production/add")}
                  >
                    <PlusOutlined />
                    Add
                  </Button>
                  <Button
                    type="primary"
                    disabled={selectedRowKeys?.length === 0}
                    className="mr-2 w-[104px] h-[30px] font-medium"
                    onClick={() => setShowModal(true)}
                  >
                    <DeleteOutlined />
                    Delete
                  </Button>
                </div>
                {hasRole(1, 2, 3) && (
                  <div>
                    <Button
                      type="primary"
                      className="w-[162px] h-[30px]"
                      onClick={() => {
                        setIsModalOpen(true);
                        form.setFieldValue("ReportFile", 1);
                      }}
                      disabled={selectedRowKeys.length === 0}
                    >
                      <FaFileAlt className="!text-[14px] font-medium" />
                      Production Report
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Form>

          <ConfirmModal
            showModal={showModal}
            setShowModal={setShowModal}
            onDeleteConfirm={onDeleteConfirm}
            setSelectedRowKeys={setSelectedRowKeys}
          />

          <GlobalTable
            columns={DeviceProductionColumns(
              hoveredRow,
              setShowModal,
              setSelectedRowKeys,
              setParams,
              params,
            )}
            dataSource={listData}
            rowSelection={rowSelection}
            pagination={paginationConfig}
            onRow={(record, rowIndex) => ({
              onMouseEnter: () => setHoveredRow(rowIndex),
              onMouseLeave: () => setHoveredRow(null),
              ...onRow(record, rowIndex),
            })}
            loading={loading}
          />
        </div>
      </div>

      <Modal
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <FaFileAlt className="text-[14px] text-black pl-1 " />
            <span className="text-[14px] font-[Poppins] ml-2">
              Production Report
            </span>
          </div>
        }
        open={isModalOpen}
        closable={false}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={580}
        centered
        destroyOnClose
      >
        <Form
          validateTrigger={"onChange"}
          form={form}
          onFinish={() => {}}
          scrollToFirstError
          layout={"vertical"}
          autoComplete={"off"}
        >
          <div className="bg-white rounded-[4px] overflow-hidden">
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col span={24}>
                  <CustomSelect
                    label="Report File"
                    name="ReportFile"
                    options={[
                      { label: "CSV", value: 1 },
                      { label: "PDF", value: 2 },
                    ]}
                    placeholder="Please select"
                    required
                    allowClear={false}
                    defaultValue={reportFile}
                    onChange={(_, value) => {
                      setReportFile(value);
                    }}
                  />
                </Col>
              </Row>
            </div>
            <div className="flex justify-center space-x-2 mt-2.5">
              <Button
                type="primary"
                className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
                onClick={() => {
                  setShouldFetch(true);
                  setTimeout(() => {
                    handleExportClick(form.getFieldValue("ReportFile"));
                  }, 200);
                }}
                loading={exportLoading}
              >
                <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                <span>Apply</span>
              </Button>
              <Button
                className="reset-button"
                onClick={() => setIsModalOpen(false)}
              >
                <CloseOutlined />
                <span>Cancel</span>
              </Button>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceProduction;
